import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import SeoManager from './components/SeoManager';
import MarketingTracking from './components/MarketingTracking';
import Footer from './components/Footer';
import AboutUs from './pages/AboutUs';
import PeopleInvolved from './pages/PeopleInvolved';
import JobsCareers from './pages/JobsCareers';
import Downloads from './pages/Downloads';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import RefundPolicy from './pages/RefundPolicy';
import TermsOfUse from './pages/TermsOfUse';
import HowToUse from './pages/HowToUse';
import DeleteData from './pages/DeleteData';
import PublicReviews from './pages/PublicReviews';
import StartImporting from './pages/StartImporting';
import HowItWorks from './pages/HowItWorks';
import { capturePublicAttribution } from './services/marketingAttribution';

const GA_MEASUREMENT_ID = 'G-S613C1E4NK';

function trackGaPageView(pagePath) {
  if (typeof window === 'undefined') return;

  const gtag =
    window.gtag ??
    function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    };

  gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title
  });
}

function RouteAnalytics() {
  const location = useLocation();
  const lastPagePathRef = useRef(null);

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    if (lastPagePathRef.current === pagePath) return;
    lastPagePathRef.current = pagePath;
    trackGaPageView(pagePath);
    capturePublicAttribution(location.pathname === '/downloads' ? 'download_page_view' : 'landing_page_view');
  }, [location.pathname, location.search, location.hash]);

  return null;
}

// Home page component
const Home = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Welcome to <span className="text-primary">Checkstall</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
              Connecting Nigerians to Chinese factories and helping rural merchants bring products to light
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/about"
                className="bg-primary text-white px-8 py-3 rounded-2xl font-semibold hover:bg-primary-600 transition-colors duration-200"
              >
                Learn More
              </a>
              <a
                href="/contact"
                className="border-2 border-primary text-primary px-8 py-3 rounded-2xl font-semibold hover:bg-primary hover:text-white transition-colors duration-200"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Choose Checkstall?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We make international trade simple, secure, and profitable for Nigerian entrepreneurs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Global Sourcing</h3>
              <p className="text-gray-600">
                Connect directly with verified Chinese manufacturers and access thousands of quality products
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quality Assurance</h3>
              <p className="text-gray-600">
                Rigorous quality checks and verification processes ensure you receive only the best products
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Secure Payments</h3>
              <p className="text-gray-600">
                Safe and flexible payment options with full transaction protection and support
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your International Trade Journey?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            Join thousands of Nigerian entrepreneurs who trust Checkstall for their international sourcing needs
          </p>
          <a
            href="/contact"
            className="bg-white text-primary px-8 py-3 rounded-2xl font-semibold hover:bg-gray-50 transition-colors duration-200"
          >
            Get Started Today
          </a>
        </div>
      </section>
    </div>
  );
};

// Exported for route/SEO coverage tests without parsing React source.
export function AppRoutes() {
  return (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/people" element={<PeopleInvolved />} />
          <Route path="/careers" element={<JobsCareers />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/how-to-use" element={<HowToUse />} />
          <Route path="/delete-data" element={<DeleteData />} />
          <Route path="/reviews" element={<PublicReviews />} />
          <Route path="/start-importing" element={<StartImporting />} />
        </Routes>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <SeoManager />
        <RouteAnalytics />
        <MarketingTracking />
        <Navigation />
        <AppRoutes />
        <Footer />
      </div>
    </Router>
  );
}

export default App;
